import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { wireAccountNameEnquiry } from '../../forms/account-name-enquiry';
import {
  ACCOUNT_NAME_ENQUIRY_FALLBACK_MESSAGE,
  AccountNameEnquiryResult,
  toAccountNameEnquiryResult,
} from '../../models/account-name-enquiry';
import { Button } from '../button/button';
import { InputComponent } from '../input/input';
import { Select } from '../select/select';
import { UploadCard } from '../upload-card/upload-card';
import { BankService } from '../../services/bank-service';
import {
  ActivationFundingAccount,
  ActivationFundingSource,
  DisbursementActivationService,
} from '../../services/disbursement-activation.service';
import { SECUREPAY_SHARED_CONFIG } from '../../config/securepay-shared-config';
import { HelperService, Notification } from '@shared';

interface NipBank {
  institutionCode: string;
  institutionName: string;
}

interface SavedFundingAccount extends ActivationFundingAccount {
  id: string;
  accountName: string;
  status: string;
  expanded: boolean;
  existing: boolean;
}

interface MandateModalState {
  fundingSource: ActivationFundingSource;
  completesSetup: boolean;
}

@Component({
  selector: 'app-activate-disbursement',
  standalone: true,
  imports: [ReactiveFormsModule, Button, InputComponent, Select, UploadCard],
  templateUrl: './activate-disbursement.html',
})
export class ActivateDisbursement implements OnInit {
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private bankService = inject(BankService);
  private activationService = inject(DisbursementActivationService);
  private config = inject(SECUREPAY_SHARED_CONFIG);
  private destroyRef = inject(DestroyRef);
  private notificationService = inject(Notification);
  private helperService = inject(HelperService);

  merchantId = '';
  backRoute = '/main/disbursement/active';
  homeRoute = '/main/disbursement/active';
  submitting = signal(false);
  termsAccepted = signal(false);
  showTermsModal = signal(false);
  banks = signal<NipBank[]>([]);
  banksLoading = signal(false);

  readonly minDate = new Date().toISOString().slice(0, 10);

  readonly sanctionOptions = [
    { label: 'Clear', value: 'clear' },
    { label: 'Not Clear', value: 'not_clear' },
  ];

  readonly fundingModelOptions = [
    { label: 'Per-run Debit', value: 'per_run_debit' },
  ];

  readonly labelClass = '!text-sm !font-medium !text-gray-700 !mb-1';
  readonly inputClass =
    '!w-full !border !border-gray-200 !bg-white !rounded-md !px-3 !py-2.5 !text-sm';

  readonly form = this.fb.group({
    justificationNarrative: ['', Validators.required],
    directors: this.fb.array<FormGroup>([]),
  });

  readonly fundingAccountForm = this.fb.group({
    fundingModel: ['per_run_debit', Validators.required],
    accountNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    bankName: ['', Validators.required],
    accountName: ['', Validators.required],
    amountThreshold: [null as number | null, [Validators.required, Validators.min(1)]],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    isDefault: [false],
  });

  savedFundingAccounts = signal<SavedFundingAccount[]>([]);
  addingFundingAccount = signal(false);
  mandateModal = signal<MandateModalState | null>(null);
  confirmingActivationFee = signal(false);

  /**
   * Keeps this screen's own `/api/Disbursement/name-inquiry` endpoint rather
   * than the shared `verify-account` one. The custom `verify` also absorbs this
   * form's quirk that the bank select binds the institution *name*, not the code.
   */
  protected readonly accountNameEnquiry = wireAccountNameEnquiry({
    accountNumber: this.fundingAccountForm.controls.accountNumber,
    bankCode: this.fundingAccountForm.controls.bankName,
    accountName: this.fundingAccountForm.controls.accountName,
    destroyRef: this.destroyRef,
    verify: ({ accountNumber, bankCode: institutionName }) => {
      const bank = this.findBank(institutionName);
      if (!bank) {
        return of<AccountNameEnquiryResult>({
          resolved: false,
          message: 'Bank list is still loading. Please retry in a moment.',
        });
      }
      return this.activationService
        .nameInquiry(this.config.apiBaseUrl, {
          accountNumber,
          channelCode: 1,
          destinationInstitutionCode: bank.institutionCode,
          stagingStatus: !this.config.production,
        })
        .pipe(
          map((res) => toAccountNameEnquiryResult(res)),
          catchError(() =>
            of<AccountNameEnquiryResult>({
              resolved: false,
              message: ACCOUNT_NAME_ENQUIRY_FALLBACK_MESSAGE,
            }),
          ),
        );
    },
  });

  @Output() formSubmitted = new EventEmitter<void>();

  cacStandingReport = signal<File | null>(null);
  sourceOfFundsAttestation = signal<File | null>(null);
  secRegistrarLicence = signal<File | null>(null);
  secRegisteredLicenseNumber = signal<File | null>(null);
  activationId = signal('');

  ngOnInit(): void {
    this.merchantId = this.route.snapshot.paramMap.get('merchantId') ?? '';
    const data = this.route.snapshot.data;
    if (data['backRoute']) this.backRoute = data['backRoute'];
    if (data['homeRoute']) this.homeRoute = data['homeRoute'];
    this.loadBanks();
    this.enableDisbursement();
    this.loadOwners();
    this.loadExistingFundingAccounts();
  }

  control(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  accountControl(name: keyof typeof this.fundingAccountForm.controls): FormControl {
    return this.fundingAccountForm.controls[name] as FormControl;
  }

  get directorsArray(): FormArray {
    return this.form.get('directors') as FormArray;
  }

  directorControl(index: number, field: string): FormControl {
    return (this.directorsArray.at(index) as FormGroup).get(field) as FormControl;
  }

  private buildDirectorGroup(directorName = ''): FormGroup {
    return this.fb.group({
      directorName: [directorName, Validators.required],
      sanctionResult: ['', Validators.required],
    });
  }

  private loadOwners(): void {
    this.activationService
      .getOwners(this.config.apiBaseUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const owners = res.data?.owners ?? [];
          this.directorsArray.clear();
          if (owners.length > 0) {
            owners.forEach((o) => {
              const name = [o.firstName, o.lastName].filter(Boolean).join(' ');
              this.directorsArray.push(this.buildDirectorGroup(name));
            });
          } else {
            this.directorsArray.push(this.buildDirectorGroup());
            this.directorsArray.push(this.buildDirectorGroup());
          }
        },
        error: () => {
          this.directorsArray.push(this.buildDirectorGroup());
          this.directorsArray.push(this.buildDirectorGroup());
        },
      });
  }

  onFileChange(
    target: 'cacStandingReport' | 'sourceOfFundsAttestation' | 'secRegistrarLicence' | 'secRegisteredLicenseNumber',
    file: File | File[] | null,
  ): void {
    const single = Array.isArray(file) ? (file[0] ?? null) : file;
    this[target].set(single);
  }

  private loadExistingFundingAccounts(): void {
    this.activationService
      .getFundingSourceRecords(this.config.apiBaseUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const existing: SavedFundingAccount[] = (res.data?.data ?? []).map((record) => ({
            id: record.id,
            accountNumber: record.accountNumber,
            accountName: record.accountName,
            bankCode: record.bankCode,
            bankName: record.bankName,
            amountThreshold: record.amountThreshold,
            startDate: record.startDate ?? '',
            endDate: record.endDate ?? '',
            isDefault: record.isDefault,
            status: record.status,
            expanded: false,
            existing: true,
          }));
          this.savedFundingAccounts.update((added) => [...existing, ...added]);
        },
        error: (err: HttpErrorResponse) =>
          this.notificationService.error(
            'Error',
            err.error?.message || 'Failed to load your funding accounts.',
          ),
      });
  }

  onAddFundingAccount(): void {
    if (this.addingFundingAccount()) return;
    const account = this.readFundingAccountForm();
    if (!account) return;

    this.addingFundingAccount.set(true);
    this.activationService
      .createFundingSource(this.config.apiBaseUrl, {
        accountNumber: account.accountNumber,
        bankCode: account.bankCode,
        bankName: account.bankName,
        perTransactionLimit: account.amountThreshold,
        startDate: account.startDate,
        endDate: account.endDate,
        setAsDefault: account.isDefault,
      })
      .pipe(
        finalize(() => this.addingFundingAccount.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => this.saveFundingAccount(account, res.data),
        error: (err: HttpErrorResponse) => {
          const rejected = (err.error?.data ?? null) as ActivationFundingSource | null;
          if (err.status === 400 && rejected?.status === 'Rejected') {
            this.notificationService.error(
              'Direct debit failed',
              err.error?.message || rejected.statusReason || 'The bank declined the direct-debit mandate.',
            );
            this.saveFundingAccount(account, rejected);
            return;
          }
          this.notificationService.error(
            'Unable to add funding account',
            err.error?.message || 'Something went wrong. Please try again.',
          );
        },
      });
  }

  toggleDefaultFundingAccount(): void {
    const control = this.fundingAccountForm.controls.isDefault;
    control.setValue(!control.value);
  }

  toggleFundingAccountCard(index: number): void {
    this.savedFundingAccounts.update((accounts) =>
      accounts.map((account, i) => (i === index ? { ...account, expanded: !account.expanded } : account)),
    );
  }

  copyActivationAccountNumber(): void {
    const accountNumber = this.mandateModal()?.fundingSource.activationInstructions?.accountNumber;
    if (accountNumber) this.helperService.copyToClipBoard(accountNumber);
  }

  onActivationFeeSent(): void {
    const modal = this.mandateModal();
    if (!modal || this.confirmingActivationFee()) return;

    this.confirmingActivationFee.set(true);
    this.activationService
      .confirmActivationFeeSent(this.config.apiBaseUrl, modal.fundingSource.id)
      .pipe(
        finalize(() => this.confirmingActivationFee.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.updateSavedStatus(res.data);
          this.notificationService.success(
            'Activation fee sent',
            res.message || "We'll notify you when the account is active.",
          );
          this.closeMandateModal();
        },
        error: (err: HttpErrorResponse) =>
          this.notificationService.error(
            'Error',
            err.error?.message || 'Failed to confirm the activation fee.',
          ),
      });
  }

  closeMandateModal(): void {
    const completesSetup = this.mandateModal()?.completesSetup;
    this.mandateModal.set(null);
    if (completesSetup) this.formSubmitted.emit();
  }

  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(value: string): string {
    return value ? value.slice(0, 10).split('-').reverse().join('-') : '—';
  }


  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.error('Kindly fill all fields', '');
      return;
    }
    const fundingAccounts = this.collectFundingAccounts();
    if (!fundingAccounts) return;

    this.submitting.set(true);
    this.activationService
      .configure(this.config.apiBaseUrl, this.activationId(), this.buildPayload(fundingAccounts))
      .pipe(
        finalize(() => this.submitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.notificationService.error('Error', res.message ?? 'An error occurred');
            return;
          }
          const savedIds = new Set(this.savedFundingAccounts().map((account) => account.id));
          const pending = res.data?.fundingAccounts?.find(
            (account) =>
              !savedIds.has(account.id) && account.status === 'Pending' && !!account.activationInstructions,
          );
          if (pending) {
            this.mandateModal.set({ fundingSource: pending, completesSetup: true });
          } else {
            this.formSubmitted.emit();
          }
        },
        error: (err) =>
          this.notificationService.error(
            'Error',
            err?.error?.message ?? err?.message ?? 'An error occurred',
          ),
      });
  }

  private collectFundingAccounts(): ActivationFundingAccount[] | null {
    let accounts: ActivationFundingAccount[] = this.savedFundingAccounts()
      .filter((account) => !account.existing)
      .map(({ accountNumber, bankCode, bankName, amountThreshold, startDate, endDate, isDefault }) => ({
        accountNumber, bankCode, bankName, amountThreshold, startDate, endDate, isDefault,
      }));

    const hasUnsavedInput = !!this.fundingAccountForm.controls.accountNumber.value;
    if (!this.savedFundingAccounts().length || hasUnsavedInput) {
      const current = this.readFundingAccountForm();
      if (!current) return null;
      if (current.isDefault) {
        accounts = accounts.map((account) => ({ ...account, isDefault: false }));
      }
      accounts.push(current);
    }

    if (accounts.length > 1 && accounts.filter((account) => account.isDefault).length !== 1) {
      this.notificationService.error(
        'Choose a default funding account',
        'Turn on "Set as default funding account" for one of your accounts.',
      );
      return null;
    }
    return accounts;
  }

  private readFundingAccountForm(): (ActivationFundingAccount & { accountName: string }) | null {
    const form = this.fundingAccountForm;
    if (form.invalid) {
      form.markAllAsTouched();
      this.notificationService.error('Complete the funding account details', '');
      return null;
    }

    const v = form.getRawValue();
    if (v.endDate! <= v.startDate!) {
      this.notificationService.error('Invalid dates', 'End date must be after the start date.');
      return null;
    }

    return {
      accountNumber: v.accountNumber!,
      bankCode: this.findBank(v.bankName!)?.institutionCode ?? '',
      bankName: v.bankName!,
      accountName: v.accountName!,
      amountThreshold: Number(v.amountThreshold),
      startDate: v.startDate!,
      endDate: v.endDate!,
      isDefault: !!v.isDefault,
    };
  }

  private saveFundingAccount(
    account: ActivationFundingAccount & { accountName: string },
    source: ActivationFundingSource,
  ): void {
    this.savedFundingAccounts.update((accounts) => [
      ...(account.isDefault ? accounts.map((saved) => ({ ...saved, isDefault: false })) : accounts),
      { ...account, id: source.id, status: source.status, expanded: true, existing: false },
    ]);
    this.fundingAccountForm.reset({ fundingModel: 'per_run_debit', isDefault: false });

    if (source.status === 'Pending' && source.activationInstructions) {
      this.mandateModal.set({ fundingSource: source, completesSetup: false });
    }
  }

  private updateSavedStatus(source: ActivationFundingSource): void {
    this.savedFundingAccounts.update((accounts) =>
      accounts.map((account) => (account.id === source.id ? { ...account, status: source.status } : account)),
    );
  }

  private findBank(institutionName: string): NipBank | undefined {
    return this.banks().find((bank) => bank.institutionName === institutionName);
  }

  private buildPayload(fundingAccounts: ActivationFundingAccount[]): FormData {
    const v = this.form.value;
    const fd = new FormData();

    fd.append('justificationNarrative', v.justificationNarrative ?? '');
    fd.append('serviceProvider', 'A');
    fd.append('fundingModel', this.fundingAccountForm.controls.fundingModel.value ?? '');
    if (fundingAccounts.length) {
      fd.append('FundingAccounts', JSON.stringify(fundingAccounts));
    }
    const secLicenseNum = this.secRegisteredLicenseNumber();
    if (secLicenseNum) fd.append('secRegisteredLicenseNumber', secLicenseNum, secLicenseNum.name);

    const directors = (v.directors ?? []).map((d: any) => ({
      directorName: d?.directorName ?? '',
      sanctionResult: d?.sanctionResult ?? '',
    }));
    fd.append('directorScreenings', JSON.stringify(directors));

    const cac = this.cacStandingReport();
    if (cac) fd.append('cacBindingReport', cac, cac.name);

    const sofa = this.sourceOfFundsAttestation();
    if (sofa) fd.append('sourceOfFundsAttestation', sofa, sofa.name);

    const sec = this.secRegistrarLicence();
    if (sec) fd.append('secRegisteredLicense', sec, sec.name);

    return fd;
  }

  private loadBanks(): void {
    this.banksLoading.set(true);
    this.bankService
      .getBankList(this.config.apiBaseUrl)
      .pipe(
        finalize(() => this.banksLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (list: unknown) => {
          if (!Array.isArray(list)) return;
          this.banks.set(
            list.map((b) => {
              const o = b as Record<string, unknown>;
              return {
                institutionCode: String(o['institutionCode'] ?? ''),
                institutionName: String(o['institutionName'] ?? ''),
              };
            }),
          );
        },
      });
  }

  enableDisbursement(): void {
    this.activationService.activate(this.config.apiBaseUrl).subscribe({
      next: (res) => {
        if (res.data?.activationId) {
          this.activationId.set(res.data.activationId);
        }
      },
    });
  }

}
